import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Dropdown} from 'antd';
import {getViewportPopupLayout} from './viewportLayout';

const isPopupScroll = (target) =>
  target instanceof Element &&
  Boolean(
    target.closest(
      '.ant-dropdown, .ant-select-dropdown, .viewport-dropdown-scroll',
    ),
  );

const ViewportDropdown = ({
  children,
  overlayClassName,
  overlayStyle,
  onOpenChange,
  placement: placementProp,
  dropdownRender,
  ...rest
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(placementProp || 'bottomLeft');
  const [maxHeight, setMaxHeight] = useState(undefined);

  const applyLayout = useCallback(() => {
    const next = getViewportPopupLayout(anchorRef.current);
    const nextPlacement = placementProp || next.placement;
    setPlacement((current) =>
      current === nextPlacement ? current : nextPlacement,
    );
    setMaxHeight((current) =>
      current === next.maxHeight ? current : next.maxHeight,
    );
  }, [placementProp]);

  useLayoutEffect(() => {
    if (open) {
      applyLayout();
    }
  }, [open, applyLayout]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onLayout = (event) => {
      if (isPopupScroll(event?.target)) {
        return;
      }
      applyLayout();
    };
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, applyLayout]);

  return (
    <Dropdown
      {...rest}
      placement={placement}
      autoAdjustOverflow={{
        adjustX: true,
        adjustY: false,
        shiftY: true,
      }}
      overlayClassName={['viewport-actions-menu', overlayClassName]
        .filter(Boolean)
        .join(' ')}
      overlayStyle={overlayStyle}
      dropdownRender={(menu) => (
        <div
          className='viewport-dropdown-scroll'
          style={{maxHeight, overflowY: 'auto'}}>
          {dropdownRender ? dropdownRender(menu) : menu}
        </div>
      )}
      onOpenChange={(nextOpen, info) => {
        if (nextOpen) {
          applyLayout();
        }
        setOpen(nextOpen);
        onOpenChange?.(nextOpen, info);
      }}>
      <span
        ref={anchorRef}
        className='viewport-dropdown-anchor'
        onPointerDownCapture={applyLayout}>
        {children}
      </span>
    </Dropdown>
  );
};

export default ViewportDropdown;
